import { PopUpMessageContext, useApp, useAuth, useCollection } from '@hb/react'
import {
  BooleanField,
  Collection,
  ConsentFormTemplate,
  consentFormTemplatesCollection,
  copyableConsentFormsCollection,
  defaultInitialValue,
  DropdownField,
  FieldMap,
  FieldMapValue,
  FieldTypes,
  FileField,
  IdField,
  OnUploadProgress,
  PracticeConsentForm,
  Template,
  TextField,
  WithId,
  WithoutMetadata,
} from '@hb/shared'
import { FORM_ERROR } from 'final-form'
import { useCallback, useContext, useMemo } from 'react'
import { addItem } from '../../../../backend/db/utils/collections'
import { processFieldMapData } from '../../../../backend/db/utils/processData'
import { SimpleForm } from '../../../forms'
import { OnFormSubmit } from '../../../forms/FinalForm/types'
import { StandaloneTemplatePreview } from '../../TemplatePreview'
import { addExternalPdfTemplate, copyExternalPdfTemplateToPractice } from '../../utils'

const modeField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  options: [
    { id: 'template', text: 'Copy from a template from Hamilton Billing (you can edit it later)' },
    { id: 'new', text: 'Create a consent form from scratch' },
  ],
  placeholder: 'Mode',
}

const templateField: IdField<ConsentFormTemplate> = {
  type: FieldTypes.ID,
  collection: copyableConsentFormsCollection,
  condition: v => v?.mode === 'template',
  placeholder: 'Template',
  renderOption: (v: WithId<Template>) => (
    <StandaloneTemplatePreview
      practiceId={null}
      collection={consentFormTemplatesCollection}
      templateType="consentForm"
      item={v}
    />
  ),
}

const isExternalPdfField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  // inputType: 'switch',
  yesText: 'Yes',
  noText: 'No',
  placeholder: 'Create from PDF?',
}

const practiceIsExternalPdfField: BooleanField = {
  ...isExternalPdfField,
  condition: v => v?.mode === 'new',
}

const externalPdfField: FileField = {
  type: FieldTypes.FILE,
  placeholder: 'Consent Form PDF',
  fileType: 'pdf',
  name: 'externalPdf',
  condition: v => !!v?.isExternalPdf,
}

const practiceExternalPdfField: FileField = {
  ...externalPdfField,
  condition: v => v?.mode === 'new' && !!v?.isExternalPdf,
}

const nameField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Consent Form Name',
}

const practiceTemplateField: FieldMap = {
  name: 'New Template',
  children: {
    name: nameField,
    mode: modeField,
    template: templateField,
    isExternalPdf: practiceIsExternalPdfField,
    externalPdf: practiceExternalPdfField,
  },
}

const adminTemplateField: FieldMap = {
  name: 'New Template',
  children: {
    name: nameField,
    isExternalPdf: isExternalPdfField,
    externalPdf: externalPdfField,
  },
}

export const AddConsentForm = <T extends PracticeConsentForm | ConsentFormTemplate>({
  practiceId,
  collection,
  onClose,
}: {
  practiceId: string | null
  collection: Collection<T>
  onClose: () => void
}) => {
  const { appName } = useApp()
  const { authUser, admin } = useAuth()
  const { items: templates } = useCollection(consentFormTemplatesCollection)
  const field = useMemo(
    () => (practiceId ? practiceTemplateField : adminTemplateField),
    [practiceId],
  )

  const { processResponse } = useContext(PopUpMessageContext)

  const onCreateFromTemplate = useCallback(
    async (values: FieldMapValue) => {
      const uid = authUser?.uid
      if (!uid) {
        return { [FORM_ERROR]: 'Not signed in' }
      }
      if (!practiceId) {
        return { [FORM_ERROR]: 'Practice ID not found' }
      }
      // create the consent form
      const selectedTemplate = templates?.find(t => t.id === values.template)
      if (!selectedTemplate) {
        return { [FORM_ERROR]: 'Template not found' }
      }

      try {
        if (selectedTemplate.isExternalPdf) {
          if (!selectedTemplate.externalPdf) {
            return { [FORM_ERROR]: 'No base PDF found for template' }
          }
          await copyExternalPdfTemplateToPractice({
            appName,
            name: values.name,
            practiceId,
            templateId: selectedTemplate.id,
            templateType: 'consentForm',
          })
        } else {
          const newConsentForm: WithoutMetadata<Omit<PracticeConsentForm, 'rank'>> = {
            ...selectedTemplate,
            name: values.name,
            isExternalPdf: false,
            externalPdf: null,
          }
          await addItem(appName, collection, newConsentForm)
        }
      } catch (err: any) {
        return processResponse({ error: err?.message || 'An error occurred' })
      }
      const res = processResponse({ success: 'Consent form created' })
      onClose()
      return res
    },
    [appName, authUser, collection, processResponse, templates, onClose, practiceId],
  )

  const onCreateFromScratch = useCallback(
    async (values: FieldMapValue, onUploadProgress: OnUploadProgress) => {
      const uid = authUser?.uid
      if (!uid) {
        return { [FORM_ERROR]: 'Not signed in' }
      }

      try {
        const now = new Date().toISOString()

        if (values.isExternalPdf) {
          if (!values.externalPdf) {
            return { [FORM_ERROR]: 'Uploaded consent form selected but no PDF found' }
          }
          const processed = await processFieldMapData(
            appName,
            admin,
            uid,
            `temp/templates/${now}`,
            field,
            values,
            null,
            onUploadProgress,
          )
          await addExternalPdfTemplate({
            templateType: 'consentForm',
            name: processed.name,
            isExternalPdf: processed.isExternalPdf,
            externalPdf: processed.externalPdf,
            practiceId,
          })
        } else {
          await addItem(appName, collection, {
            ...values,
            editorVersion: 'v2',
            templateText: defaultInitialValue,
          })
        }
      } catch (err: any) {
        return processResponse({ error: err?.message || 'An error occurred' })
      }
      const res = processResponse({ success: 'Consent form created' })
      onClose()
      return res
    },
    [appName, authUser, collection, processResponse, onClose, admin, field, practiceId],
  )

  const onCreate: OnFormSubmit<FieldMapValue> = useCallback(
    async (values, onUploadProgress) => {
      if (values.mode === 'template') {
        return onCreateFromTemplate(values)
      } else {
        return onCreateFromScratch(values, onUploadProgress)
      }
    },
    [onCreateFromTemplate, onCreateFromScratch],
  )
  return <SimpleForm field={field} onSubmit={onCreate} theme="detailed" />
}
